import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Layout from "../components/Layout.jsx";
import { employeeService } from "../services/apiService.jsx";
import { getProfilePhotoUrl, debugImageUrl } from "../utils/imageUtils.js";
import "./Employees.css";

// Employee Details Modal Component
const EmployeeDetailsModal = ({ employee, onClose }) => {
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);

  const photoUrl = getProfilePhotoUrl(employee.profile_photo);

  // Debug logging
  useEffect(() => {
    if (employee.profile_photo) {
      debugImageUrl(employee.profile_photo);
    }
  }, [employee.profile_photo]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return isNaN(d) ? '-' : d.toLocaleDateString();
  };

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} role="dialog">
      <div className="modal-dialog modal-lg" role="document">
        <div className="modal-content">
          <div className="modal-header bg-light border-bottom">
            <h5 className="modal-title">Employee Details</h5>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <div className="card">
              <div className="card-body">
                <div className="row">
                  {/* Employee Photo Section */}
                  <div className="col-md-3 text-center mb-4">
                    {photoUrl && !imageError ? (
                      <>
                        {imageLoading && (
                          <div
                            className="rounded-circle bg-light d-inline-flex align-items-center justify-content-center"
                            style={{
                              width: '150px',
                              height: '150px',
                              border: '4px solid #007bff'
                            }}
                          >
                            <div className="spinner-border text-primary" role="status" style={{ width: '30px', height: '30px' }}>
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        )}
                        <img
                          src={photoUrl}
                          alt={`${employee.first_name} ${employee.last_name}`}
                          className="rounded-circle"
                          style={{
                            width: '150px',
                            height: '150px',
                            objectFit: 'cover',
                            border: '4px solid #007bff',
                            display: imageLoading ? 'none' : 'block'
                          }}
                          onLoad={() => setImageLoading(false)}
                          onError={(e) => {
                            console.error(`Failed to load image from: ${photoUrl}`);
                            setImageError(true);
                            setImageLoading(false);
                          }}
                        />
                      </>
                    ) : (
                      <div
                        className="rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center"
                        style={{
                          width: '150px',
                          height: '150px',
                          color: 'white',
                          fontSize: '48px'
                        }}
                      >
                        <i className="bi bi-person"></i>
                      </div>
                    )}
                    {photoUrl && imageError && (
                      <p style={{ fontSize: '0.75rem', color: '#999', marginTop: '8px' }}>
                        Image not available
                      </p>
                    )}
                  </div>

                  {/* Employee Details Section */}
                  <div className="col-md-9">
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Employee Name:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>
                          {employee.first_name} {employee.last_name}
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Email ID:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>{employee.email_id || '-'}</p>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Designation:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>{employee.Designation || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Department:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>{employee.department_name || '-'}</p>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Employee ID:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>{employee.employee_code || employee.employee_id || '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Location:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>{employee.location || '-'}</p>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Status:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>
                          <span className={`badge bg-${employee.status === 'Active' ? 'success' : 'danger'}`}>
                            {employee.status || '-'}
                          </span>
                        </p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Joining Date:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>{formatDate(employee.Joiningdate || employee.joining_date)}</p>
                      </div>
                    </div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Birth Date:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>{formatDate(employee.birthday)}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="mb-1" style={{ fontSize: '0.875rem', color: '#666' }}>
                          <strong>Reporting Manager:</strong>
                        </p>
                        <p style={{ fontSize: '1rem' }}>
                          {employee.reporting_manager_name || employee.reporting_manager || '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Employees = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  useEffect(() => {
    fetchEmployees();
  }, [user?.role]);

  const fetchEmployees = () => {
    const role = String(user?.role || "").toLowerCase().trim();
    const employeesRequest = role === "hod" || role.includes("head")
      ? employeeService.getHodReportingEmployees()
      : role === "team lead" || role === "teamleader" || role === "tl" || role.includes("lead")
        ? employeeService.getReportingEmployees()
        : employeeService.getAllEmployees();

    employeesRequest
      .then((res) => {
        let arr = [];
        if (Array.isArray(res?.data)) {
          arr = res.data;
        } else if (Array.isArray(res?.data?.data)) {
          arr = res.data.data;
        } else {
          console.error("No employees array found in API response:", res);
        }
        setEmployees(arr);
      })
      .catch((error) => console.error("Error retrieving employee details:", error));
  };

  const handleEdit = (employeeId) => {
    navigate(`/update-employee/${employeeId}`);
  };

  const handleView = (employee) => {
    setSelectedEmployee(employee);
  };

  const handleCloseModal = () => {
    setSelectedEmployee(null);
  };

  const handleDelete = async (employeeId, employeeName) => {
    const confirmDelete = window.confirm(
      `Are you sure you want to delete employee ${employeeName}? This action cannot be undone.`
    );
    if (!confirmDelete) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      await employeeService.deleteEmployee(employeeId);
      fetchEmployees();
      alert("Employee deleted successfully!");
    } catch (error) {
      console.error("Error deleting employee:", error);
      let errorMsg = "Failed to delete employee";
      if (error.response) {
        if (error.response.status === 401) errorMsg = "You are not authorized. Please login again.";
        else if (error.response.status === 403) errorMsg = "You don't have permission to delete employees.";
        else if (error.response.status === 404) errorMsg = "Employee not found.";
        else errorMsg = error.response.data?.message || errorMsg;
      } else if (error.request) {
        errorMsg = "No response from server. Please check your internet connection.";
      }
      setDeleteError(errorMsg);
      alert(errorMsg);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAdd = () => {
    navigate('/createemployee');
  };

  const filteredEmployees = employees.filter((employee) => {
    // Exclude Management department employees
    if ((employee.department_name || '').toLowerCase() === 'management') return false;
    const searchLower = search.toLowerCase();
    return (
      employee.first_name?.toLowerCase().includes(searchLower) ||
      employee.last_name?.toLowerCase().includes(searchLower) ||
      employee.email_id?.toLowerCase().includes(searchLower) ||
      employee.department_name?.toLowerCase().includes(searchLower) ||
      employee.employee_code?.toLowerCase().includes(searchLower)
    );
  });

  return (
  <Layout>
    <div className="page-header">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div className="pagetitle">
              <h1 style={{ margin: 0 }}>My Employee List</h1>
            </div>
            <div className="d-flex gap-2" style={{ flexWrap: 'wrap' }}>
              {/* Search Input */}
              <div className="input-group" style={{ width: '250px' }}>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search employees..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
              </div>
              
              {/* Add Employee Button */}
              <button
                className="btn btn-success d-flex align-items-center gap-1"
                onClick={handleAdd}
              >
                <i className="bi bi-plus"></i>
                Create Employee
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <section className="section">
      <div className="fixed-height-container">
        <div className="scrollable-card">
          <div className="row">
            <div className="col-lg-12">
              <div className="card">
                <div className="card-body">
                  <div className="table-responsive-scroll">
                    <table className="table compact-table">
                      <thead>
                        <tr>
                          <th scope="col">Employee Code</th>
                          <th scope="col">First Name</th>
                          <th scope="col">Last Name</th>
                          <th scope="col">Email ID</th>
                          <th scope="col">Department</th>
                          <th scope="col">Designation</th>
                          <th scope="col">Joining Date</th>
                          <th scope="col">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredEmployees.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <i className="bi bi-people"></i>
                              <p className="mt-2">No employees available</p>
                            </td>
                          </tr>
                        ) : (
                          filteredEmployees.map((employee) => {
                            let formattedDate = "-";
                            if (employee.Joiningdate) {
                              const time = new Date(employee.Joiningdate);
                              formattedDate = isNaN(time) ? "-" : time.toLocaleDateString();
                            }
                            return (
                              <tr key={employee.employee_id} className="employee-row">
                                <td>{employee.employee_code}</td>
                                <td>{employee.first_name}</td>
                                <td>{employee.last_name}</td>
                                <td>{employee.email_id}</td>
                                <td>{employee.department_name}</td>
                                <td>{employee.Designation || employee.role || '-'}</td>
                                <td>{formattedDate}</td>
                                <td>
                                  <div className="d-flex gap-1 justify-content-center">
                                    <button
                                      className="btn btn-sm btn-info text-white"
                                      onClick={() => handleView(employee)}
                                      title="View Details"
                                    >
                                      <i className="bi bi-eye"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-primary"
                                      onClick={() => handleEdit(employee.employee_id)}
                                      title="Edit Employee"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-danger"
                                      onClick={() => handleDelete(employee.employee_id, `${employee.first_name} ${employee.last_name}`)}
                                      disabled={isDeleting}
                                      title="Delete Employee"
                                    >
                                      <i className="bi bi-trash"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Employee Details Modal */}
    {selectedEmployee && (
      <EmployeeDetailsModal employee={selectedEmployee} onClose={handleCloseModal} />
    )}
  </Layout>
);
};

export default Employees;